import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { api } from "../lib/api";
import { daftarkanMall } from "../lib/mallRegistry";
import { useWargaAktif } from "../lib/presence";
import { FONT, glassVars } from "../components/kota/ui";
import { MallTopBar, MallTabs } from "../components/mall/MallTopBar";
import { MallStage } from "../components/mall/MallStage";
import { MallPanel, MallBeliBar } from "../components/mall/MallPanel";
import { BarangkuSheet, MallBantuanSheet } from "../components/mall/MallSheets";

const TOKEN_KEY = "jawsel_warga_token";
const LEBAR = 520;   // lebar kolom "HP" — di desktop halaman tetap tampil seperti di HP, di tengah layar
// Panel rak mengisi sisa tinggi setelah area panggung (bar atas + tab + panggung + nama) yang dibatasi 400–520 px
const panelDari = H => Math.max(220, H - Math.min(520, Math.max(400, Math.round(H * 0.58))));

// Mall Jawsel — halaman terpisah (ala Thaloria Mall, gaya kaca Jawsel): bar atas · toggle Rumah | Butik Avatar · panggung
// barang terpilih · panel rak (chip kategori + grid 3 kolom) · bar beli lengket. "Coba di rumahku" → /rumah/saya?coba=<id>
// (ruang dalam dengan barang di tangan → Beli & Simpan). ?kota=<id> untuk tombol kembali, ?dari=rumah bila datang dari rumah.
export default function Mall() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const kota = sp.get("kota") || "kediri", dariRumah = sp.get("dari") === "rumah";
  useWargaAktif("mall");
  const token = localStorage.getItem(TOKEN_KEY);
  const [kat, setKat] = useState(null), [tas, setTas] = useState(null), [avatar, setAvatar] = useState(null);
  const [tab, setTab] = useState(sp.get("tab") === "aksesoris" ? "aksesoris" : "rumah"), [filter, setFilter] = useState("semua");
  const [pilihId, setPilihId] = useState(null), [cobaAvatar, setCobaAvatar] = useState(true);
  const [sheet, setSheet] = useState(null), [busy, setBusy] = useState(false);
  const [panelH, setPanelH] = useState(() => panelDari(window.innerHeight));
  useEffect(() => { const f = () => setPanelH(panelDari(window.innerHeight)); window.addEventListener("resize", f); return () => window.removeEventListener("resize", f); }, []);

  const [sets, setSets] = useState([]);
  const muatTas = () => { if (token) api.mallTas(token).then(setTas).catch(() => setTas({ items: [], saldo: null, dipakai: [] })); };
  useEffect(() => {
    api.mallKatalog().then(d => { daftarkanMall(d.items); setKat(d); }).catch(e => toast.error(e.message));
    api.mallSet().then(d => setSets(d.sets || [])).catch(() => {});
    muatTas();
    if (token) api.alunAvatar(token).then(a => setAvatar(a.avatar)).catch(() => {});
  }, []); // eslint-disable-line

  const items = useMemo(() => (kat?.items || []).filter(i => i.jenis === tab && i.kategori !== "kendaraan"), [kat, tab]);   // kendaraan dijual di Dealer (/dealer)
  const pilih = useMemo(() => (kat?.items || []).find(i => i.id === pilihId) || null, [kat, pilihId]);
  // ?item= (sekali, saat katalog tiba) memindahkan tab ke jenis barang itu; selebihnya barang terpilih selalu dari tab aktif
  const awalRef = useRef(sp.get("item"));
  useEffect(() => {
    if (!kat) return;
    const awal = awalRef.current && kat.items.find(i => i.id === awalRef.current);
    awalRef.current = null;
    if (awal) { setTab(awal.jenis); setPilihId(awal.id); return; }
    const kini = kat.items.find(i => i.id === pilihId);
    if (!kini || kini.jenis !== tab) setPilihId(items[0]?.id || null);
  }, [kat, tab, items, pilihId]);
  const punya = useMemo(() => Object.fromEntries((tas?.items || []).map(i => [i.id, i.jumlah])), [tas]);
  const dipakai = useMemo(() => new Set(tas?.dipakai || []), [tas]);
  const saldo = tas?.saldo;

  const keKartu = () => { toast("Urus Kartu Warga dulu di Balai Warga untuk berbelanja"); nav(`/profil?kota=${kota}`); };
  const gantiTab = t => { setTab(t); setFilter("semua"); };
  const beli = async it => {
    setBusy(true);
    try {
      const r = await api.mallBeli(token, it.id);
      setTas(t => ({ ...(t || { items: [], dipakai: [] }), saldo: r.saldo, items: [...(t?.items || []).filter(x => x.id !== it.id), { ...it, jumlah: r.jumlah }] }));
      if (it.jenis === "rumah") toast.success(`${it.nama} dibeli · sisa ${r.saldo} koin`, { action: { label: "Tata di Rumahku", onClick: () => nav("/rumah/saya") } });
      else toast.success(`${it.nama} dibeli · sisa ${r.saldo} koin — ketuk Pakai untuk memakainya`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const beliSet = async s => {
    setBusy(true);
    try {
      const r = await api.mallBeliSet(token, s.key);
      muatTas();
      setTas(t => (t ? { ...t, saldo: r.saldo } : t));
      toast.success(`Paket ${s.nama} dibeli · hemat ${s.harga_normal - s.harga_bundel} koin · sisa ${r.saldo} koin`, { action: { label: "Tata di Rumahku", onClick: () => nav("/rumah/saya") } });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const pakai = async it => {
    setBusy(true);
    try { const r = await api.mallPakai(token, it.id); setTas(t => ({ ...t, dipakai: r.dipakai })); setAvatar(r.avatar); setCobaAvatar(true); toast.success(r.dipakai.includes(it.id) ? `${it.nama} dipakai` : `${it.nama} dilepas`); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const coba = it => { if (!token) return keKartu(); nav(`/rumah/saya?coba=${it.id}`); };
  const kembali = () => nav(dariRumah && token ? "/rumah/saya" : `/kota/${kota}`);

  return (
    <div data-testid="mall-page" style={{ position: "fixed", inset: 0, overflow: "hidden", background: "#1d1d1f", display: "flex", justifyContent: "center", fontFamily: FONT, ...glassVars(false) }}>
      <div data-testid="mall-kolom" style={{ position: "relative", width: "100%", maxWidth: LEBAR, height: "100%", overflow: "hidden", background: "#f3efe3", color: "#141414", boxShadow: "0 0 60px rgba(0,0,0,.45)" }}>
        <MallStage item={pilih} avatar={avatar} cobaAvatar={cobaAvatar} panelH={panelH} punya={pilih ? punya[pilih.id] || 0 : 0} dipakai={pilih ? dipakai.has(pilih.id) : false} onCoba={() => coba(pilih)} onToggleAvatar={() => setCobaAvatar(v => !v)} />
        <MallTopBar saldo={saldo} punya={tas?.items.length || 0} onBack={kembali} onBarangku={() => setSheet("barangku")} onBantuan={() => setSheet("bantuan")} />
        <MallTabs aktif={tab} onPilih={gantiTab} />
        <MallPanel panelH={panelH} kat={kat} tab={tab} filter={filter} onFilter={setFilter} items={items} pilih={pilih} onPilih={it => setPilihId(it.id)} punya={punya} dipakai={dipakai} sets={sets} token={token} busy={busy} onBeliSet={beliSet}>
          <MallBeliBar item={pilih} token={token} saldo={saldo} punya={pilih ? punya[pilih.id] || 0 : 0} dipakai={pilih ? dipakai.has(pilih.id) : false} busy={busy} onBeli={() => beli(pilih)} onPakai={() => pakai(pilih)} onKartu={keKartu} />
        </MallPanel>
        {sheet === "barangku" && <BarangkuSheet token={token} tas={tas} avatar={avatar} busy={busy} onPakai={pakai} onPilih={it => { setSheet(null); setPilihId(it.id); }} onKeRumah={() => nav("/rumah/saya")} onKartu={keKartu} onClose={() => setSheet(null)} />}
        {sheet === "bantuan" && <MallBantuanSheet onClose={() => setSheet(null)} />}
      </div>
      <Toaster theme="light" position="top-center" />
    </div>
  );
}
